import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import {
  ClaimBreakdownCategoryPayload,
  ClaimBreakdownPaymentMethodsPayload,
  ClaimDetailsPayload,
} from '../EmployerServiceCategories.types';

const useGetClaimDetailsQuery = (planId: string) => {
  const { isLoading, data, isError, refetch } = useQuery(
    [API_URL.ACCOUNT_USAGE_PLANS_CLAIM_DETAILS_BY_PLAN_ID(''), planId],
    () => api.get(API_URL.ACCOUNT_USAGE_PLANS_CLAIM_DETAILS_BY_PLAN_ID(planId)),
    {
      retry: 0,
      enabled: !!planId,
    }
  );

  const claimDetails = useMemo<ClaimDetailsPayload>(() => {
    const responseData = data?.data;

    if (!responseData) {
      return {
        total: 0,
        categories: [],
        paymentMethods: [],
      };
    }

    const categoriesMap = new Map<string, ClaimBreakdownCategoryPayload>();
    (responseData.category_breakdown || []).forEach((item) => {
      const key = item.category_type || '';
      const existing = categoriesMap.get(key);
      if (existing) {
        existing.amount += item.amount || 0;
        existing.percentage += item.percentage || 0;
        existing.claimCount += item.claim_count || 0;
      } else {
        categoriesMap.set(key, {
          categoryType: item.category_type!,
          amount: item.amount || 0,
          percentage: item.percentage || 0,
          claimCount: item.claim_count || 0,
        });
      }
    });
    const categories = Array.from(categoriesMap.values())
      .filter((item) => item.amount > 0)
      .sort((a, b) => b.percentage - a.percentage);

    const paymentMethodsMap = new Map<string, ClaimBreakdownPaymentMethodsPayload>();
    (responseData.payment_method_breakdown || []).forEach((item) => {
      const key = item.category || '';
      const existing = paymentMethodsMap.get(key);
      if (existing) {
        existing.amount += item.amount || 0;
        existing.percentage += item.percentage || 0;
      } else {
        paymentMethodsMap.set(key, {
          category: item.category!,
          description: item.description || '',
          amount: item.amount || 0,
          percentage: item.percentage || 0,
        });
      }
    });
    const paymentMethods = Array.from(paymentMethodsMap.values())
      .filter((item) => item.amount > 0)
      .sort((a, b) => b.percentage - a.percentage);

    return {
      total: responseData.total_paid_claims_count || 0,
      categories,
      paymentMethods,
    };
  }, [data]);

  return { isLoading, claimDetails, isError, refetch };
};

export default useGetClaimDetailsQuery;
